// Hur den pågående aktivitetens tid VISAS.
//
// Två skilda val som får kombineras fritt:
//   FORM    ringen som fylls, cirkeln som fylls, tårtbiten, urtavla med visare,
//           liggande stapel, stående stapel — eller ingen figur alls
//   SIFFROR om mm:ss skrivs ut ovanpå/inuti formen
//
// VALET ÄGS AV HANDLEDAREN och bor på kontot (users.timer_display_shape /
// timer_display_digits, V007). localStorage-nyckeln nedan är en SPEGLING av kontots
// värde, inte en egen sanning: den skrivs när användaren läses in (AppContext) och
// läses av vyerna. Skälet till att spegeln finns kvar är reaktiviteten och starten —
// timern ska ha rätt form direkt vid första rendering, utan att vänta på servern.
//
// Speglar childMode.ts och npfNudges.ts: värdet ligger i localStorage och efter
// setItem dispatchas 'storage' manuellt, eftersom webbläsaren annars bara fyrar
// eventet i ANDRA flikar — inte i den som gjorde ändringen.
//
// Default är ring MED siffror — appens ursprungliga utseende. Ett saknat ELLER okänt
// värde tolkas alltid som defaulten; vi kastar aldrig och visar aldrig ingen timer.
const TIMER_DISPLAY_KEY = 'timerDisplay';

export type TimerDisplayShape = 'ring' | 'disc' | 'wedge' | 'dial' | 'bar_h' | 'bar_v' | 'none';

export type TimerDisplay = {
  shape: TimerDisplayShape;
  /** Om mm:ss skrivs ut. */
  digits: boolean;
};

export const DEFAULT_TIMER_DISPLAY: TimerDisplay = { shape: 'ring', digits: true };

/** Alla former i den ordning de visas i inställningarna. */
export const TIMER_DISPLAY_SHAPES: readonly TimerDisplayShape[] = [
  'ring',
  'disc',
  'wedge',
  'dial',
  'bar_h',
  'bar_v',
  'none',
];

const isShape = (value: unknown): value is TimerDisplayShape =>
  typeof value === 'string' && (TIMER_DISPLAY_SHAPES as readonly string[]).includes(value);

/**
 * Tolkar form + siffror utifrån (serversvar, klientobjekt, localStorage).
 *
 * Utan figur MÅSTE siffrorna vara på — annars vore ytan tom. Samma regel står som
 * CHECK i databasen; den upprepas här för att en klient aldrig ska rita ingenting
 * ens om den får ett omöjligt värde.
 */
export const normalizeTimerDisplay = (
  shape?: string | null,
  digits?: boolean | null,
): TimerDisplay => {
  const safeShape: TimerDisplayShape = isShape(shape) ? shape : DEFAULT_TIMER_DISPLAY.shape;
  const safeDigits = digits === undefined || digits === null ? true : digits;
  return { shape: safeShape, digits: safeShape === 'none' ? true : safeDigits };
};

export const getTimerDisplay = (): TimerDisplay => {
  const stored = localStorage.getItem(TIMER_DISPLAY_KEY);
  if (!stored) return DEFAULT_TIMER_DISPLAY;
  try {
    const parsed = JSON.parse(stored);
    return normalizeTimerDisplay(parsed?.shape, parsed?.digits);
  } catch {
    return DEFAULT_TIMER_DISPLAY;
  }
};

export const setTimerDisplay = (display: TimerDisplay): void => {
  localStorage.setItem(
    TIMER_DISPLAY_KEY,
    JSON.stringify(normalizeTimerDisplay(display.shape, display.digits)),
  );
  window.dispatchEvent(new Event('storage'));
};
